import React, {useState} from 'react';
import {Spinner} from 'react-bootstrap';
import {CircularProgress} from '@mui/material';
import {toast} from 'react-toastify';
import {deleteParkedCart, restoreParkedCart} from '../../infrastructure/parkedCartApi';
import {isFailure, isPending, isSuccess} from '../../../common/domain/RemoteData.model';
import {fetchClienteByNumCliente, useComercialContext} from '../../../comercial/ComercialContext';
import {useCartContext} from '../../context/CartContext';
import {reconcileCart} from '../../../order/infrastructure/OrderApi';
import {useUserContext} from '../../../user/infrastructure/UserContext';

const formatSavedAt = (createdAt) => {
    if (!createdAt) {
        return '';
    }
    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleString('es-ES', {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'});
};

const RestoreParkedCartButton = ({parkedCart, onRestored, isComercial}) => {
    const [loading, setLoading] = useState(false);
    const {setActingAsCliente, clearActingAsCliente} = useComercialContext();
    const {loadCartBis} = useCartContext();

    const handleClick = async () => {
        setLoading(true);
        try {
            const result = await restoreParkedCart(parkedCart.id);
            if (!isSuccess(result)) {
                toast.error('No se pudo recuperar el carrito guardado');
                return;
            }
            // Solo comercial: el carrito activo ya es el del cliente guardado y hay que
            // dejar el selector "actuando como" apuntando a ese mismo cliente. Si el cliente
            // ya no está en la cartera (403), se limpia el selector para que un add
            // posterior no re-etiquete el carrito a otro cliente. Un cliente normal no
            // tiene selector: su carrito guardado es siempre de su propio cliente.
            if (isComercial) {
                const clienteResult = await fetchClienteByNumCliente(parkedCart.numCliente);
                if (isSuccess(clienteResult) && clienteResult.value) {
                    setActingAsCliente(clienteResult.value);
                } else {
                    clearActingAsCliente();
                    toast.warning('El cliente del carrito ya no está en tu cartera. Carrito recuperado igualmente.');
                }
            }
            // Los precios del carrito recuperado van congelados de cuando se aparcó: el
            // reconcile marca como obsoleto lo que haya caducado (precio, stock, producto).
            await reconcileCart();
            await loadCartBis();
            toast.success('Carrito recuperado');
            onRestored();
        } catch (error) {
            console.error('Failed to restore parked cart:', error);
            toast.error('No se pudo recuperar el carrito guardado');
        } finally {
            setLoading(false);
        }
    };

    return (
        <button type="button"
            className="btn btn-primary"
            onClick={handleClick}
            disabled={loading}>
            {loading ? <CircularProgress size={20} color="inherit"/> : 'Recuperar'}
        </button>
    );
};

const DeleteParkedCartButton = ({parkedCart}) => {
    const [loading, setLoading] = useState(false);

    const handleClick = async () => {
        // eslint-disable-next-line no-alert
        if (!window.confirm('¿Eliminar este carrito guardado? Sus productos se perderán.')) {
            return;
        }
        setLoading(true);
        try {
            const result = await deleteParkedCart(parkedCart.id);
            if (isSuccess(result)) {
                toast.success('Carrito guardado eliminado');
            } else {
                toast.error('No se pudo eliminar el carrito guardado');
            }
        } catch (error) {
            console.error('Failed to delete parked cart:', error);
            toast.error('No se pudo eliminar el carrito guardado');
        } finally {
            setLoading(false);
        }
    };

    return (
        <button type="button"
            className="btn btn-outline-danger"
            onClick={handleClick}
            disabled={loading}>
            {loading ? <CircularProgress size={20}/> : 'Eliminar'}
        </button>
    );
};

/**
 * Panel desplegable de carritos guardados (equivalente al PendingPreview de
 * pendientes). Para un comercial muestra TODOS los aparcados con su cliente
 * visible — ocultar los de otros clientes los haría irrecuperables —, con los
 * del cliente representado ahora mismo destacados y primero. Para un cliente
 * normal todos son de su propio cliente, así que no se pinta el cliente ni el badge.
 *
 * @param {{parkedCarts: Object, setShowPanel: (show: boolean) => void}} props
 * parkedCarts es el resultado de useParkedCarts.
 */
export const ParkedCartsPanel = ({parkedCarts, setShowPanel}) => {
    const {parkedData, parkedCarts: allParked} = parkedCarts;
    const {actingAsCliente} = useComercialContext();
    const user = useUserContext();
    const isComercial = user?.isComercial === true;

    const currentNumCliente = isComercial ? (actingAsCliente?.numCliente ?? null) : null;
    const ofCurrentClient = allParked.filter((it) => it.numCliente === currentNumCliente);
    const ofOtherClients = allParked.filter((it) => it.numCliente !== currentNumCliente);
    const sorted = [...ofCurrentClient, ...ofOtherClients];

    return (
        <div className="d-flex flex-column gap-3 p-2">
            <h2 className="h5 mb-0">Carritos guardados</h2>
            {isPending(parkedData) && (
                <div className="text-center my-3">
                    <Spinner/>
                </div>
            )}
            {isFailure(parkedData) && (
                <div className="alert alert-danger py-2 mb-0">
                    No se pudieron cargar tus carritos guardados.
                </div>
            )}
            {isSuccess(parkedData) && sorted.length === 0 && (
                <span className="text-muted">
                    No tienes carritos guardados. Guarda el carrito actual desde el panel del carrito.
                </span>
            )}
            {isSuccess(parkedData) && sorted.length > 0 && (
                <ul className="list-group">
                    {sorted.map((parkedCart) => {
                        const isCurrentClient = isComercial && parkedCart.numCliente === currentNumCliente;
                        return (
                            <li key={parkedCart.id}
                                className={`list-group-item ${isCurrentClient ? 'border-primary' : ''}`}>
                                <div className="d-flex flex-column flex-md-row align-items-md-center gap-3">
                                    <div className="flex-fill">
                                        {isComercial ? (
                                            <>
                                                <span className="fw-semibold">
                                                    {parkedCart.nombreRazonSocial ?? 'Cliente desconocido'}
                                                    {parkedCart.numCliente ? ` (${parkedCart.numCliente})` : ''}
                                                </span>
                                                {isCurrentClient && (
                                                    <span className="badge bg-primary ms-2">Cliente actual</span>
                                                )}
                                                {parkedCart.automatico && (
                                                    <span className="badge bg-secondary ms-2"
                                                          title="Se guardó solo al cambiar de cliente y vuelve solo al seleccionar de nuevo ese cliente">
                                                        Automático
                                                    </span>
                                                )}
                                                <br/>
                                                <small className="text-muted">
                                                    Guardado el {formatSavedAt(parkedCart.createdAt)}
                                                    {' · '}{parkedCart.lineCount} productos · {parkedCart.totalUnits} uds.
                                                </small>
                                            </>
                                        ) : (
                                            <>
                                                <span className="fw-semibold">
                                                    Guardado el {formatSavedAt(parkedCart.createdAt)}
                                                </span>
                                                <br/>
                                                <small className="text-muted">
                                                    {parkedCart.lineCount} productos · {parkedCart.totalUnits} uds.
                                                </small>
                                            </>
                                        )}
                                    </div>
                                    <div className="d-flex flex-row gap-2 align-items-center">
                                        <RestoreParkedCartButton
                                            parkedCart={parkedCart}
                                            onRestored={() => setShowPanel(false)}
                                            isComercial={isComercial}
                                        />
                                        <DeleteParkedCartButton parkedCart={parkedCart}/>
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};
